import { useEffect } from "react";
import { Helmet } from "react-helmet-async";

/**
 * Remove prerendered SEO tags ONLY when Helmet has already emitted a
 * replacement (tagged with data-rh). This avoids a window where we strip
 * the prerendered <title>/meta before Helmet's deferred (rAF-batched)
 * emit lands - which previously left some routes with no <title> after
 * hydration. JSON-LD from the prerender is always removed (Helmet owns it).
 */
function stripPrerenderedDuplicates() {
  if (typeof document === "undefined") return;
  const selectors = [
    "title",
    'meta[name="description"]',
    'link[rel="canonical"]',
    'meta[property="og:title"]',
    'meta[property="og:description"]',
    'meta[property="og:url"]',
    'meta[property="og:type"]',
    'meta[property="og:image"]',
    'meta[name="twitter:title"]',
    'meta[name="twitter:description"]',
    'meta[name="twitter:url"]',
    'meta[name="twitter:image"]',
    'meta[name="twitter:card"]',
  ];
  for (const sel of selectors) {
    const helmetEl = document.querySelector(`${sel}[data-rh]`);
    if (!helmetEl) continue;
    document
      .querySelectorAll(`${sel}:not([data-rh])`)
      .forEach((el) => el.parentNode?.removeChild(el));
  }
  document
    .querySelectorAll('script[type="application/ld+json"][data-prerender-path]')
    .forEach((el) => el.parentNode?.removeChild(el));
}

const SITE_URL = "https://www.tonyknows.co.uk";
// Controlled site default. Stable, brand-owned URL - never a temporary preview image.
const DEFAULT_OG_IMAGE = `${SITE_URL}/og-default.jpg`;

function withTrailingSlash(urlOrPath: string) {
  const [withoutHash, hash = ""] = urlOrPath.split("#", 2);
  const [base, query = ""] = withoutHash.split("?", 2);
  const normalizedBase = base.endsWith("/") || /\.[a-z0-9]{2,8}$/i.test(base) ? base : `${base}/`;
  return `${normalizedBase}${query ? `?${query}` : ""}${hash ? `#${hash}` : ""}`;
}

function normalizeCanonical(url: string) {
  return url.startsWith(SITE_URL) ? withTrailingSlash(url) : url;
}

interface SEOProps {
  title: string;
  description: string;
  path?: string;
  image?: string;
  noIndex?: boolean;
  canonical?: string;
  jsonLd?: Record<string, unknown> | Record<string, unknown>[];
  preloadImage?: string;
  /** Responsive preload: pairs of {href, media} for art-directed LCP (e.g. mobile vs desktop hero) */
  preloadImagesResponsive?: Array<{ href: string; media: string }>;
  /** Extra origins to preconnect to (e.g. Supabase storage for LCP hero on blog/career pages). */
  preconnectOrigins?: string[];
  publishedTime?: string;
  modifiedTime?: string;
  authorName?: string;
}

const SEO = ({
  title,
  description,
  path = "/",
  image,
  noIndex = false,
  canonical,
  jsonLd,
  preloadImage,
  preloadImagesResponsive,
  preconnectOrigins,
  publishedTime,
  modifiedTime,
  authorName,
}: SEOProps) => {
  // Helmet emits its tags via requestAnimationFrame, so we must run cleanup
  // AFTER its emit lands. Run on rAF (and a follow-up frame) so we only strip
  // prerendered tags that Helmet has actually replaced.
  useEffect(() => {
    let id2 = 0;
    const id1 = requestAnimationFrame(() => {
      stripPrerenderedDuplicates();
      id2 = requestAnimationFrame(stripPrerenderedDuplicates);
    });
    return () => {
      cancelAnimationFrame(id1);
      if (id2) cancelAnimationFrame(id2);
    };
  });

  const ogImage = image || DEFAULT_OG_IMAGE;
  const url = withTrailingSlash(`${SITE_URL}${path}`);
  const canonicalUrl = normalizeCanonical(canonical || url);
  const fullTitle = title.length > 60 ? title.slice(0, 57) + "..." : title;
  const trimmedDesc =
    description.length > 160 ? description.slice(0, 157) + "..." : description;

  const jsonLdArray = jsonLd ? (Array.isArray(jsonLd) ? jsonLd : [jsonLd]) : [];

  return (
    <Helmet>
      <title>{fullTitle}</title>
      <meta name="description" content={trimmedDesc} />
      <link rel="canonical" href={canonicalUrl} />
      {noIndex && <meta name="robots" content="noindex,nofollow" />}

      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={trimmedDesc} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:type" content={publishedTime ? "article" : "website"} />
      <meta property="og:image" content={ogImage} />
      {publishedTime && <meta property="article:published_time" content={publishedTime} />}
      {modifiedTime && <meta property="article:modified_time" content={modifiedTime} />}
      {authorName && <meta property="article:author" content={authorName} />}

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={trimmedDesc} />
      <meta name="twitter:image" content={ogImage} />

      {preconnectOrigins?.map((origin) => (
        <link key={origin} rel="preconnect" href={origin} crossOrigin="anonymous" />
      ))}

      {preloadImage && (
        <link rel="preload" as="image" href={preloadImage} fetchPriority="high" />
      )}
      {preloadImagesResponsive?.map((p) => (
        <link
          key={p.href}
          rel="preload"
          as="image"
          href={p.href}
          media={p.media}
          fetchPriority="high"
        />
      ))}

      {jsonLdArray.map((obj, i) => (
        <script key={i} type="application/ld+json">{JSON.stringify(obj)}</script>
      ))}
    </Helmet>
  );
};

export default SEO;
